import { Nullable } from "sonobello.utilities.react";

import IMedicalFormInterpreter from "./IMedicalFormInterpreter";
import MedicalFormState from "./MedicalFormState";

export interface IComposableMedicalFormInterpreterConfig {
  /** The keys within the medical form output which should be ignored when determining non-candidate status. */
  keysToIgnore: string[];
  /** A set of predicates which return true if an answer is valid, keyed by the form item ID. */
  answerPredicates: Record<string, (answer: Nullable<string>) => boolean>;
}

/** An {@link IMedicalFormInterpreter} implementation that ignores answers matching the composed keys.
 * @remarks Any form item without a configured predicate will be considered a non-candidate if answered "true".
 */
class ComposableMedicalFormInterpreter implements IMedicalFormInterpreter {
  /** Pre-set answer predicates for some form items. */
  private readonly answerPredicates: Record<string, (answer: Nullable<string>) => boolean>;

  constructor(config: IComposableMedicalFormInterpreterConfig) {
    const automaticPassPredicates = Object.fromEntries(config.keysToIgnore.map(key => [key, () => true]));
    this.answerPredicates = { ...automaticPassPredicates, ...config.answerPredicates };
  }

  isNonCandidate(medicalFormOutput: MedicalFormState) {
    return Object.entries(medicalFormOutput).some(([key, value]) => {
      const answerPredicate = this.answerPredicates[key];
      return answerPredicate ? !answerPredicate(value) : value === "true";
    });
  }
}

export default ComposableMedicalFormInterpreter;
